// src/antdviews/component/PushContent.tsx

// 推送管理组件

import axios from 'axios'
import React from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import {
    Layout,
    Tabs,
    Table,
    Select,
    Button,
    Space,
    Row,
    Col,
    Card,
    Typography,
    Tag,
    Pagination,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
    BellOutlined,
    HistoryOutlined,
    ReloadOutlined,
    MobileOutlined,
} from '@ant-design/icons'

import { Global } from '../../services/Global'
import { browserUtil } from '../../utils/BrowserUtil'
import { encrypUtil } from '../../utils/EncrypUtil'
import '../../css/antd.timeline.css'
import { DEFAULT_RPP, ROLE } from '../../services/Macro'
import WarnModal from './WarnModal'
import { ERR_CODE } from '../../services/ServerErr'
import { checker } from '../../utils/Checker'
import { Page } from '../../services/Page'
import { PushApi } from '../../services/Api'
import { mathUtil } from '../../utils/MathUtil'

const { Content } = Layout
const { Title, Text } = Typography

const warnModalStyles = {
    content: {
        width: '480px',
        top: '40%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: '-20px',
    },
}

interface ActivePushItem {
    uid: string
    uuid: string
    uname: string
    deviceToken: string
    mac: string
}

interface HistoryPushItem {
    uid: string
    uuid?: string
    uname: string
    email: string
    deviceToken: string
    mac: string
    startTime: string
    endTime: string
}

interface PushContentState {
    logPage: number
    logSize: number
    tree: any[]
    list: any[]
    totalRecords: number

    showWarn: boolean
    warnTitle: string
    warnInfo: string
    errCode: number

    isModal3Open: boolean
    activeDate: string

    activePushList: ActivePushItem[]
    recentPushTime: string
    historyPushList: HistoryPushItem[]

    pushPerPage: number
    pushCurrentPage: number
    totalPushPages: number
    totalPushRecords: number
    pages: number[]
}

class _PushContent extends React.Component<WithTranslation, PushContentState> {
    state: PushContentState = {
        logPage: 1,
        logSize: 100,
        tree: [],
        list: [],
        totalRecords: 0,

        showWarn: false,
        warnTitle: '本地错误',
        warnInfo: '参数错误',
        errCode: 0,

        isModal3Open: false,
        activeDate: '1',

        activePushList: [],
        recentPushTime: '',
        historyPushList: [],

        pushPerPage: 10,
        pushCurrentPage: 1,
        totalPushPages: 0,
        totalPushRecords: 0,
        pages: [],
    }

    startTime: string = ''
    endTime: string = ''
    isDataFetching: boolean = true
    extraTabs: string[] = []

    // =========================================================================
    // 逻辑方法
    // =========================================================================

    componentWillMount() {
        Global.isMobile = browserUtil.checkMobile()
    }

    componentDidMount() {
        const callChecker = async () => {
            const roleStr = localStorage.getItem('role') || `${ROLE.SUPER}`
            const role = roleStr === `${ROLE.SUPER}` ? ROLE.SUPER : ROLE.USER
            const page = role === ROLE.SUPER ? Page.dynamic : Page.userDynamic
            const isLogin = await checker.checkLogin(role, page)
            if (isLogin) {
                this.getActivePush()
            }
        }
        callChecker()
    }

    async getActivePush() {
        const DEBUG = true
        const TAG = `PushContent.getActivePush() - `
        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'
        const requestData = {
            role: ROLE.SUPER,
            token,
            lang: lang.split('_')[0],
        }
        DEBUG && console.log(TAG, `requestData:\n`, requestData)
        const params = await encrypUtil.encodeRequestRsa(requestData)
        DEBUG && console.log(TAG, `params:\n`, params)
        const response = await axios.post(PushApi.GET_ACTIVE_PUSH_LIST, params)
        DEBUG && console.log(TAG, `获取活跃推送列表:\n`, response.data)
        this.setState({
            activePushList: response.data.data.list || [],
            recentPushTime: response.data.data.latestPushTime || '',
        })
    }

    async getHistoryPush() {
        const DEBUG = true
        const TAG = `PushContent.getHistoryPush() - `
        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'
        const requestData = {
            role: ROLE.SUPER,
            token,
            lang: lang.split('_')[0],
            page: this.state.pushCurrentPage,
            size: this.state.pushPerPage,
        }
        DEBUG && console.log(TAG, `requestData:\n`, requestData)
        const params = await encrypUtil.encodeRequestRsa(requestData)
        DEBUG && console.log(TAG, `params:\n`, params)
        const response = await axios.post(PushApi.GET_HISTORY_PUSH_LIST, params)
        DEBUG && console.log(TAG, `获取历史推送列表:\n`, response.data)
        this.setState({
            historyPushList: response.data.data.list || [],
            recentPushTime: response.data.data.latestPushTime || '',
            totalPushPages: response.data.data.totalPages || 0,
            totalPushRecords: response.data.data.total || 0,
            pages: this.getPages(response.data.data.totalPages || 0),
        })
    }

    getPages = (totalPages: number) => {
        const logPages: number[] = []
        for (let i = 0; i < totalPages; i++) {
            logPages.push(i + 1)
        }
        return logPages
    }

    /** LPP: Push Per Page */
    onPppChange = (value: number) => {
        const oldPushPerPage = this.state.pushPerPage
        this.setState(
            {
                pushPerPage: value,
                pushCurrentPage: mathUtil.getNewPage(
                    oldPushPerPage,
                    this.state.pushCurrentPage,
                    value
                ),
            },
            () => {
                this.getHistoryPush()
            }
        )
    }

    prevPushPage = () => {
        if (this.state.pushCurrentPage > 1) {
            this.setState(
                { pushCurrentPage: this.state.pushCurrentPage - 1 },
                () => this.getHistoryPush()
            )
        }
    }

    nextPushPage = () => {
        if (this.state.pushCurrentPage < this.state.totalPushPages) {
            this.setState(
                { pushCurrentPage: this.state.pushCurrentPage + 1 },
                () => this.getHistoryPush()
            )
        }
    }

    firstPushPage = () => {
        if (this.state.pushCurrentPage > 1) {
            this.setState({ pushCurrentPage: 1 }, () => this.getHistoryPush())
        }
    }

    lastPushPage = () => {
        if (this.state.pushCurrentPage < this.state.totalPushPages) {
            this.setState(
                { pushCurrentPage: this.state.totalPushPages },
                () => this.getHistoryPush()
            )
        }
    }

    onPushPageChange = (page: number) => {
        this.setState({ pushCurrentPage: page }, () => this.getHistoryPush())
    }

    refreshActive = () => {
        this.getActivePush()
    }

    refreshHistory = () => {
        this.getHistoryPush()
    }

    // =========================================================================
    // 界面渲染
    // =========================================================================

    render() {
        if (Global.isMobile) return this.renderMobile()
        else return this.renderPc()
    }

    renderMobile() {
        return this.renderPc()
    }

    showWarn = (title: string, info: string) => {
        this.setState({
            warnTitle: title,
            warnInfo: info,
            showWarn: true,
        })
    }

    closeWarn = () => {
        const TAG = `PushContent.closeWarn() - `
        console.log(TAG, `this.state.errCode:\n`, this.state.errCode)
        if (this.state.errCode > 0) {
            if (
                this.state.errCode === ERR_CODE.NULL_TOKEN ||
                this.state.errCode === ERR_CODE.TOKEN_EXPIRED
            ) {
                window.location.href = '/mgmt'
            }
        }
        this.setState({ showWarn: false })
    }

    openAddDateTabPanel = () => {
        console.log(`Click openAddDateTabPanel`)
        this.setState({ isModal3Open: true })
    }

    onChange = (key: string) => {
        console.log(key)
        switch (key) {
            case '1':
                this.getActivePush()
                break
            case '2':
                this.getHistoryPush()
                break
        }
    }

    /** 活跃推送表格列 */
    getActiveColumns = (): ColumnsType<ActivePushItem> => {
        const t = this.props.t
        return [
            {
                title: t('uid'),
                dataIndex: 'uid',
                key: 'uid',
                width: 120,
            },
            {
                title: t('uuid'),
                dataIndex: 'uuid',
                key: 'uuid',
                width: 320,
                render: (text: string) => (
                    <Text code style={{ fontSize: 14 }}>
                        {text || '-'}
                    </Text>
                ),
            },
            {
                title: t('uname'),
                dataIndex: 'uname',
                key: 'uname',
                width: 160,
            },
            {
                title: t('deviceToken'),
                dataIndex: 'deviceToken',
                key: 'deviceToken',
                width: 320,
                render: (text: string) => <Text type="secondary">{text || '-'}</Text>,
            },
            {
                title: t('mac'),
                dataIndex: 'mac',
                key: 'mac',
                width: 180,
            },
        ]
    }

    /** 历史推送表格列 */
    getHistoryColumns = (): ColumnsType<HistoryPushItem> => {
        const t = this.props.t
        return [
            {
                title: t('uid'),
                dataIndex: 'uid',
                key: 'uid',
                width: 40,
            },
            {
                title: t('uname'),
                dataIndex: 'uname',
                key: 'uname',
                width: 60,
            },
            {
                title: t('email'),
                dataIndex: 'email',
                key: 'email',
                width: 100,
            },
            {
                title: t('deviceToken'),
                dataIndex: 'deviceToken',
                key: 'deviceToken',
                width: 340,
                render: (text: string) => <Text type="secondary">{text || '-'}</Text>,
            },
            {
                title: t('mac'),
                dataIndex: 'mac',
                key: 'mac',
                width: 100,
            },
            {
                title: t('startTime'),
                dataIndex: 'startTime',
                key: 'startTime',
                width: 120,
                render: (text: string) => <Text type="secondary">{text}</Text>,
            },
            {
                title: t('endTime'),
                dataIndex: 'endTime',
                key: 'endTime',
                width: 120,
                render: (text: string) => <Text type="secondary">{text}</Text>,
            },
        ]
    }

    renderPc() {
        const { t } = this.props

        const items = [
            {
                key: '1',
                label: (
                    <Space>
                        <BellOutlined />
                        {t('activePush')}
                    </Space>
                ),
                children: this.renderActivePush(),
            },
            {
                key: '2',
                label: (
                    <Space>
                        <HistoryOutlined />
                        {t('historyPush')}
                    </Space>
                ),
                children: this.renderHistoryPush(),
            },
        ]

        return (
            <Content
                style={{
                    margin: '4px',
                    padding: '6px',
                    background: 'var(--content-bg)',
                    minHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {this.renderWarn()}
                <Card
                    title={
                        <Space>
                            <MobileOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>
                                {t('pushMgmt')}
                            </Title>
                        </Space>
                    }
                    style={{
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        background: 'var(--card-bg)',
                    }}
                    bodyStyle={{
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    <Tabs defaultActiveKey="1" items={items} onChange={this.onChange} />
                </Card>
            </Content>
        )
    }

    renderWarn() {
        const { t } = this.props
        return (
            <WarnModal
                isMobile={Global.isMobile}
                isOpen={this.state.showWarn}
                onRequestClose={this.closeWarn}
                title={t('error')}
                subtitle={this.state.warnTitle}
                confirm={t('confirm')}
                customStyles={warnModalStyles}
            >
                {this.state.warnInfo}
            </WarnModal>
        )
    }

    /** 渲染活跃推送列表 */
    renderActivePush() {
        const { t } = this.props
        const { activePushList, recentPushTime } = this.state

        return (
            <div>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                        <Tag color="green">
                            {t('recentPushTime')}: {recentPushTime || '-'}
                        </Tag>
                        <Tag color="blue">
                            {activePushList.length} {t('records') || '条记录'}
                        </Tag>
                    </Space>
                    <Button icon={<ReloadOutlined />} onClick={this.refreshActive}>
                        {t('refresh') || '刷新'}
                    </Button>
                </div>

                <Table<ActivePushItem>
                    rowKey={(record, index) => `${record.uid}-${index}`}
                    columns={this.getActiveColumns()}
                    dataSource={activePushList}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1100 }}
                />
            </div>
        )
    }

    /** 渲染历史推送列表 */
    renderHistoryPush() {
        const { t } = this.props
        const {
            pushPerPage,
            pushCurrentPage,
            totalPushPages,
            totalPushRecords,
            historyPushList,
            recentPushTime,
        } = this.state

        // 计算表格滚动高度
        const tableScrollY = window.innerHeight - 380
        const rowHeight = 56
        const headerHeight = 38
        const availableHeight = tableScrollY - headerHeight
        const totalContentHeight = historyPushList.length * rowHeight
        const needScroll = totalContentHeight > availableHeight

        return (
            <div>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                        <Tag color="green">
                            {t('recentPushTime')}: {recentPushTime || '-'}
                        </Tag>
                        <Tag color="blue">
                            {totalPushRecords} {t('records') || '条记录'}
                        </Tag>
                    </Space>
                    <Button icon={<ReloadOutlined />} onClick={this.refreshHistory}>
                        {t('refresh') || '刷新'}
                    </Button>
                </div>

                <div style={{ flex: 1, minHeight: 0 }}>
                    <Table<HistoryPushItem>
                        rowKey={(record, index) => `${record.uid}-${index}`}
                        columns={this.getHistoryColumns()}
                        dataSource={historyPushList}
                        pagination={false}
                        bordered
                        size="small"
                        style={{ borderRadius: 6 }}
                        scroll={{
                            x: 1320,
                            y: needScroll ? (tableScrollY > 200 ? tableScrollY : 300) : undefined,
                        }}
                    />
                </div>

                {/* 分页区域 */}
                <Row
                    justify="space-between"
                    align="middle"
                    style={{
                        marginTop: 16,
                        flexShrink: 0,
                        paddingTop: 8,
                        borderTop: '1px solid #f0f0f0',
                    }}
                >
                    <Col>
                        <Text type="secondary">
                            {t('table.footer.totalRecords') || '共'} {totalPushRecords}{' '}
                            {t('table.footer.records') || '条记录'}，
                            {t('table.footer.currentPage') || '当前第'} {pushCurrentPage}/{totalPushPages}{' '}
                            {t('table.footer.page') || ''}
                        </Text>
                    </Col>
                    <Col>
                        <Space>
                            <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                {t('table.footer.pageSize') || '每页'}
                            </span>
                            <Select
                                value={pushPerPage}
                                onChange={this.onPppChange}
                                style={{ width: 80 }}
                                options={DEFAULT_RPP.map((item: number) => ({
                                    label: item,
                                    value: item,
                                }))}
                            />
                            <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                {t('table.footer.items') || '条'}
                            </span>
                            <Pagination
                                current={pushCurrentPage}
                                total={totalPushRecords}
                                pageSize={pushPerPage}
                                onChange={this.onPushPageChange}
                                showSizeChanger={false}
                                showQuickJumper
                                locale={{
                                    items_per_page: t('table.footer.itemsPerPage') || '条/页',
                                    jump_to: t('table.footer.jumpTo') || '跳至',
                                    jump_to_confirm: t('table.footer.confirm') || '确定',
                                    page: t('table.footer.page') || '',
                                    prev_page: t('table.footer.prevPage') || '上一页',
                                    next_page: t('table.footer.nextPage') || '下一页',
                                    prev_5: t('table.footer.prev5') || '向前 5 页',
                                    next_5: t('table.footer.next5') || '向后 5 页',
                                    prev_3: t('table.footer.prev3') || '向前 3 页',
                                    next_3: t('table.footer.next3') || '向后 3 页',
                                }}
                            />
                        </Space>
                    </Col>
                </Row>
            </div>
        )
    }
}

export const PushContent = withTranslation()(_PushContent)